<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Registro masivo de movimientos</h1>
    </div>

    <div>
      <b-card no-body class="custom-card" style="max-width: 900px">
        <b-card-header class="custom-card-header d-flex align-items-center">
          <h3>
            <span v-if="currentStep === 1">Paso 1: Concepto y monto</span>
            <span v-if="currentStep === 2">Paso 2: Seleccionar empleados</span>
            <span v-if="currentStep === 3">Paso 3: Confirmar y registrar</span>
          </h3>
        </b-card-header>

        <b-card-body class="custom-card-body p-0">
          <section class="form-section mb-0">

            <!-- PASO 1 -->
            <div v-if="currentStep === 1">
              <div class="row">

                <!-- Concepto -->
                <div class="col-md-12">
                  <div class="form-group">
                    <label>Concepto <span class="text-danger">*</span></label>
                    <SearchableDropdown
                      :options="conceptOptions"
                      :oldValue="bulkForm.conceptId"
                      placeholder="Seleccionar concepto"
                      @selected="onConceptSelected"
                    />
                    <FormError
                      v-if="step1Errors.conceptId"
                      message="El concepto es requerido"
                    />
                    <div v-if="selectedConcept" class="mt-2">
                      <span :class="`badge ${selectedConcept.type === 'DESCUENTO' ? 'badge-danger' : 'badge-green'} mr-2`">
                        {{ selectedConcept.type === 'DESCUENTO' ? 'Descuento' : 'Adicional' }}
                      </span>
                      <span class="text-muted small">{{ getNatureLabel(selectedConcept.nature) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Período: Mes -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label>Mes <span class="text-danger">*</span></label>
                    <FormSelect :items="monthOptions" v-model="bulkForm.periodMonth" defaultOption="Seleccione mes" />
                    <FormError v-if="step1Errors.periodMonth" message="El mes es requerido" />
                  </div>
                </div>

                <!-- Período: Año -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label>Año <span class="text-danger">*</span></label>
                    <FormSelect :items="yearOptions" v-model="bulkForm.periodYear" defaultOption="Seleccione año" />
                    <FormError v-if="step1Errors.periodYear" message="El año es requerido" />
                  </div>
                </div>

                <!-- Moneda -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label>Moneda</label>
                    <FormSelect :items="currencyOptions" v-model="bulkForm.currency" />
                  </div>
                </div>

                <!-- Monto -->
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Monto <span class="text-danger">*</span></label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      v-model="bulkForm.amount"
                      placeholder="0.00"
                      class="custom-form-control"
                      :class="{ 'custom-form-control-error': step1Errors.amount }"
                    />
                    <FormError v-if="step1Errors.amount" message="El monto debe ser mayor a 0" />
                  </div>
                </div>

                <!-- Descripción -->
                <div class="col-md-12">
                  <div class="form-group">
                    <label>Descripción</label>
                    <textarea v-model="bulkForm.description" rows="2" placeholder="Descripción opcional" class="custom-form-control"></textarea>
                  </div>
                </div>

              </div>
              <div class="form-group-buttons text-right">
                <router-link :to="{ name: 'payroll-movement-list' }" class="button button-light">Cancelar</router-link>
                <button type="button" class="button button-primary" @click="goStep2">Siguiente</button>
              </div>
            </div>

            <!-- PASO 2 -->
            <div v-if="currentStep === 2">
              <!-- Search filter -->
              <div class="mb-3">
                <input
                  type="text"
                  v-model="employeeSearch"
                  placeholder="Buscar por nombre o documento"
                  class="custom-form-control"
                  style="max-width: 400px"
                />
              </div>

              <div class="d-flex align-items-center mb-2">
                <span class="mr-3 text-muted">{{ selectedEmployeeIds.length }} empleado(s) seleccionado(s)</span>
                <button type="button" class="button button-light button-sm mr-2" @click="selectAll">Seleccionar todos</button>
                <button type="button" class="button button-light button-sm" @click="deselectAll">Deseleccionar todos</button>
              </div>

              <div class="table-responsive">
                <table class="custom-table w-100">
                  <thead>
                    <tr>
                      <th style="width: 40px"></th>
                      <th>EMPLEADO</th>
                      <th>DOCUMENTO</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="filteredEmployees.length === 0">
                      <td colspan="3" class="text-center text-muted py-4">No se encontraron empleados</td>
                    </tr>
                    <tr v-for="emp in filteredEmployees" :key="emp.key" style="cursor:pointer" @click="toggleEmployee(emp.key)">
                      <td>
                        <input type="checkbox" :checked="selectedEmployeeIds.includes(emp.key)" @click.stop="toggleEmployee(emp.key)" />
                      </td>
                      <td>{{ emp.name }}</td>
                      <td>{{ emp.identifier }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <FormError v-if="step2Error" message="Debe seleccionar al menos un empleado" />

              <div class="form-group-buttons text-right mt-3">
                <button type="button" class="button button-light mr-2" @click="currentStep = 1">Anterior</button>
                <button type="button" class="button button-primary" @click="goStep3">Siguiente</button>
              </div>
            </div>

            <!-- PASO 3 -->
            <div v-if="currentStep === 3">
              <div class="alert alert-info">
                <strong>Resumen del registro masivo</strong><br />
                <div class="mt-2">
                  <span>Concepto: <strong>{{ selectedConcept ? selectedConcept.name : '-' }}</strong></span>
                  <span v-if="selectedConcept" :class="`badge ml-2 ${selectedConcept.type === 'DESCUENTO' ? 'badge-danger' : 'badge-green'}`">
                    {{ selectedConcept.type === 'DESCUENTO' ? 'Descuento' : 'Adicional' }}
                  </span>
                </div>
                <div>Monto: <strong>{{ bulkForm.currency }} {{ Number(bulkForm.amount).toLocaleString('es-PE', { minimumFractionDigits: 2 }) }}</strong></div>
                <div>Período: <strong>{{ getMonthName(bulkForm.periodMonth) }} {{ bulkForm.periodYear }}</strong></div>
                <div>Empleados: <strong>{{ selectedEmployeeIds.length }}</strong></div>
              </div>

              <!-- Result after save -->
              <div v-if="bulkResult" class="mt-3">
                <div class="alert" :class="bulkResult.errors > 0 ? 'alert-warning' : 'alert-success'">
                  <strong>{{ bulkResult.processed }}</strong> movimiento(s) registrado(s).
                  <span v-if="bulkResult.errors > 0"><strong>{{ bulkResult.errors }}</strong> error(es).</span>
                </div>
                <ul v-if="bulkResult.errorMessages && bulkResult.errorMessages.length" class="list-unstyled">
                  <li v-for="(msg, idx) in bulkResult.errorMessages" :key="idx" class="text-danger small">
                    <i class="ri-error-warning-line mr-1"></i>{{ msg }}
                  </li>
                </ul>
              </div>

              <div class="form-group-buttons text-right mt-3">
                <button v-if="!bulkResult" type="button" class="button button-light mr-2" @click="currentStep = 2">Anterior</button>
                <button
                  v-if="!bulkResult"
                  type="button"
                  class="button button-primary"
                  @click="saveBulk"
                >
                  Confirmar y registrar
                </button>
                <router-link v-if="bulkResult" :to="{ name: 'payroll-movement-list' }" class="button button-primary">
                  Ver lista
                </router-link>
              </div>
            </div>

          </section>
        </b-card-body>
      </b-card>
    </div>
  </div>
</template>

<script>
import { Alert, FormError, FormSelect, SearchableDropdown } from 'wize-admin'
import { PayrollMovementService, PayrollConceptService, EmployeeService } from '@/core/services'
import { Constants as LocalConstants } from '@/core/utils'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

export default {
  name: 'PayrollMovementBulk',
  components: { FormError, FormSelect, SearchableDropdown },

  data() {
    const now = new Date()
    return {
      currentStep:     1,
      conceptOptions:  [],
      allConcepts:     [],
      selectedConcept: null,
      allEmployees:    [],
      employeeSearch:  '',
      selectedEmployeeIds: [],
      bulkResult:      null,
      step1Errors:     { conceptId: false, periodMonth: false, periodYear: false, amount: false },
      step2Error:      false,
      monthOptions: MONTH_NAMES.map((m, i) => ({ key: i + 1, label: m })),
      currencyOptions: [
        { key: 'PEN', label: 'PEN - Soles' },
        { key: 'USD', label: 'USD - Dólares' },
      ],
      bulkForm: {
        conceptId:   null,
        periodMonth: now.getMonth() + 1,
        periodYear:  now.getFullYear(),
        currency:    'PEN',
        amount:      null,
        description: null,
      },
    }
  },

  computed: {
    yearOptions() {
      const current = new Date().getFullYear()
      return Array.from({ length: 5 }, (_, i) => ({ key: current - 2 + i, label: String(current - 2 + i) }))
    },
    filteredEmployees() {
      if (!this.employeeSearch) return this.allEmployees
      const q = this.employeeSearch.toLowerCase()
      return this.allEmployees.filter(e =>
        (e.name || '').toLowerCase().includes(q) ||
        (e.identifier || '').toLowerCase().includes(q)
      )
    },
  },

  methods: {
    onConceptSelected(id) {
      this.bulkForm.conceptId  = id
      this.selectedConcept     = this.allConcepts.find(c => c.id === id) || null
    },

    getNatureLabel(nature) {
      const n = LocalConstants.PayrollConceptNature[nature]
      return n ? n.label : nature
    },

    getMonthName(month) {
      return MONTH_NAMES[(month || 1) - 1] || ''
    },

    goStep2() {
      this.step1Errors = {
        conceptId:   !this.bulkForm.conceptId,
        periodMonth: !this.bulkForm.periodMonth,
        periodYear:  !this.bulkForm.periodYear,
        amount:      !this.bulkForm.amount || Number(this.bulkForm.amount) <= 0,
      }
      if (Object.values(this.step1Errors).some(Boolean)) return
      this.currentStep = 2
    },

    goStep3() {
      this.step2Error = this.selectedEmployeeIds.length === 0
      if (this.step2Error) return
      this.currentStep = 3
    },

    toggleEmployee(id) {
      const idx = this.selectedEmployeeIds.indexOf(id)
      if (idx === -1) this.selectedEmployeeIds.push(id)
      else this.selectedEmployeeIds.splice(idx, 1)
    },

    selectAll() {
      this.selectedEmployeeIds = this.filteredEmployees.map(e => e.key)
    },

    deselectAll() {
      this.selectedEmployeeIds = []
    },

    async saveBulk() {
      try {
        await this.$store.dispatch('app/loading', true)
        const data = {
          conceptId:   this.bulkForm.conceptId,
          periodYear:  this.bulkForm.periodYear,
          periodMonth: this.bulkForm.periodMonth,
          amount:      Number(this.bulkForm.amount),
          currency:    this.bulkForm.currency,
          description: this.bulkForm.description || null,
          employeeIds: this.selectedEmployeeIds,
        }
        const resp = await PayrollMovementService.createBulk(data)
        this.bulkResult = resp.payload
        if (this.bulkResult.errors === 0) {
          Alert.success(`${this.bulkResult.processed} movimiento(s) registrado(s) correctamente`)
        }
      } catch (ex) {
        Alert.error(ex.errors ? ex.errors.message : 'Error al registrar los movimientos')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },

  async created() {
    try {
      const [conceptsResp, employeesResp] = await Promise.all([
        PayrollConceptService.listActive(),
        EmployeeService.listAll(),
      ])
      this.allConcepts     = conceptsResp.payload || []
      this.conceptOptions  = this.allConcepts.map(c => ({ key: c.id, label: `${c.code} - ${c.name}` }))
      this.allEmployees    = (employeesResp.payload || []).map(e => ({
        key:        e.id,
        name:       e.description,
        identifier: e.identifier || '',
      }))
    } catch (e) {
      console.error('Error al cargar catálogos', e)
    }
  },
}
</script>

<style lang="stylus" scoped></style>
